import apiClient from '../utils/apiClient';

const BASE_URL = '/walkin-drives';

/** Build a human-readable salary string from the numeric LPA fields. */
const buildSalary = (min, max) => {
  if (min != null && max != null) return `₹${min} – ${max} LPA`;
  if (min != null) return `₹${min}+ LPA`;
  if (max != null) return `Up to ₹${max} LPA`;
  return '';
};

/**
 * Convert a backend WalkinDrive record into the shape the list/detail UI
 * components expect. Top-level columns (title, company, venue …) come straight
 * off the record; all the rich nested content lives in `meta_data`.
 */
export const mapDriveFromApi = (record) => {
  const meta = record.meta_data || {};
  return {
    // identity + top-level columns
    id: record.slug, // `id` doubles as the URL slug across the UI
    slug: record.slug,
    title: record.title,
    company: record.company,
    venue: record.venue,
    city: record.city || '',
    status: record.status,
    salaryMinLpa: record.salary_min_lpa,
    salaryMaxLpa: record.salary_max_lpa,
    startDate: record.start_date,
    endDate: record.end_date,
    createdAt: record.created_at,
    viewsCount: record.views_count,

    // rich content (stored in meta_data JSON)
    salary: meta.salary || buildSalary(record.salary_min_lpa, record.salary_max_lpa),
    mapUrl: meta.mapUrl || '',
    naukriUrl: meta.naukriUrl || '',
    contactPerson: meta.contactPerson || '',
    timeAndVenue: meta.timeAndVenue || null,
    overview: meta.overview || [],
    meta: meta.meta || {},
    rolesOpen: meta.rolesOpen || [],
    responsibilities: meta.responsibilities || [],
    requirements: meta.requirements || [],
    perks: meta.perks || [],
    walkInDetails: meta.walkInDetails || '',
    importantNote: meta.importantNote || '',
    keySkills: meta.keySkills || [],
    about: meta.about || '',
  };
};

export const walkinDriveService = {
  /** Public — list active drives. */
  async list() {
    const { data } = await apiClient.get(`${BASE_URL}/`);
    return (data || []).map(mapDriveFromApi);
  },

  /** Public — fetch a single drive by slug. */
  async get(slug) {
    const { data } = await apiClient.get(`${BASE_URL}/${encodeURIComponent(slug)}`);
    return mapDriveFromApi(data);
  },

  /** Admin (walkin_admin / admin) — create a drive. */
  async create(payload) {
    const { data } = await apiClient.post(`${BASE_URL}/`, payload);
    return mapDriveFromApi(data);
  },

  /** Admin — update a drive by slug. */
  async update(slug, payload) {
    const { data } = await apiClient.patch(`${BASE_URL}/${slug}`, payload);
    return mapDriveFromApi(data);
  },

  /** Admin — delete a drive by slug. */
  async remove(slug) {
    await apiClient.delete(`${BASE_URL}/${slug}`);
  },
};

export default walkinDriveService;
